import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './WriteArticlePage.css';
import { UserContext } from '../../context/UserContext';
import ApiService from '../../services/ApiService';
import NotificationService from '../../components/NotificationService/NotificationService';

// TextQuoteSelector fuzzy matching helper
// Finds text using exact match with prefix/suffix context for disambiguation
const findTextQuoteMatch = (plainText, { exact, prefix, suffix, highlightedText }) => {
  const searchText = exact || highlightedText;
  if (!searchText) return null;

  // Strategy 1: If we have prefix and suffix, find exact text with context
  if (prefix && suffix) {
    const contextPattern = prefix + searchText + suffix;
    let idx = plainText.indexOf(contextPattern);
    if (idx !== -1) {
      return {
        start: idx + prefix.length,
        end: idx + prefix.length + searchText.length,
        matchedText: searchText
      };
    }

    // Try with just prefix context
    const prefixPattern = prefix + searchText;
    idx = plainText.indexOf(prefixPattern);
    if (idx !== -1) {
      return {
        start: idx + prefix.length,
        end: idx + prefix.length + searchText.length,
        matchedText: searchText
      };
    }

    // Try with just suffix context
    const suffixPattern = searchText + suffix;
    idx = plainText.indexOf(suffixPattern);
    if (idx !== -1) {
      return {
        start: idx,
        end: idx + searchText.length,
        matchedText: searchText
      };
    }
  }

  // Strategy 2: Fuzzy prefix/suffix matching (handles minor text changes)
  if (prefix || suffix) {
    const allMatches = [];
    let searchStart = 0;
    while (true) {
      const idx = plainText.indexOf(searchText, searchStart);
      if (idx === -1) break;

      // Score this match based on context similarity
      let score = 0;
      if (prefix) {
        const actualPrefix = plainText.substring(Math.max(0, idx - prefix.length), idx);
        // Count matching characters from the end of prefix
        for (let i = 0; i < Math.min(prefix.length, actualPrefix.length); i++) {
          if (prefix[prefix.length - 1 - i] === actualPrefix[actualPrefix.length - 1 - i]) {
            score++;
          } else {
            break;
          }
        }
      }
      if (suffix) {
        const actualSuffix = plainText.substring(idx + searchText.length, idx + searchText.length + suffix.length);
        // Count matching characters from the start of suffix
        for (let i = 0; i < Math.min(suffix.length, actualSuffix.length); i++) {
          if (suffix[i] === actualSuffix[i]) {
            score++;
          } else {
            break;
          }
        }
      }

      allMatches.push({ idx, score });
      searchStart = idx + 1;
    }

    if (allMatches.length > 0) {
      // Return the match with highest context score
      allMatches.sort((a, b) => b.score - a.score);
      const best = allMatches[0];
      return {
        start: best.idx,
        end: best.idx + searchText.length,
        matchedText: searchText
      };
    }
  }

  // Strategy 3: Direct text search (fallback)
  const idx = plainText.indexOf(searchText);
  if (idx !== -1) {
    return {
      start: idx,
      end: idx + searchText.length,
      matchedText: searchText
    };
  }

  // Strategy 4: Normalized text search (handles whitespace differences)
  const normalizedSearch = searchText.replace(/\s+/g, ' ').trim();
  const normalizedPlain = plainText.replace(/\s+/g, ' ');
  const normalizedIdx = normalizedPlain.indexOf(normalizedSearch);
  if (normalizedIdx !== -1) {
    // Map back to original position (approximate)
    return {
      start: normalizedIdx,
      end: normalizedIdx + normalizedSearch.length,
      matchedText: normalizedSearch,
      normalized: true
    };
  }

  return null;
};

const MIN_WORDS = 500;
const MAX_WORDS = 1500;
const MIN_TAGS = 5;
const MAX_TAGS = 10;

const WriteArticlePage = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const topicId = searchParams.get('topic');
  const viewArticleId = searchParams.get('view'); // Read-only mode for viewing articles
  const isReadMode = !!viewArticleId;

  // State
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState(null);
  const [articleTitle, setArticleTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [publishAs, setPublishAs] = useState('community');
  const [wordCount, setWordCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [articleAuthor, setArticleAuthor] = useState('');
  const [articleDate, setArticleDate] = useState(null);
  // Revision mode state
  const [isRevisionMode, setIsRevisionMode] = useState(false);
  const [revisionComments, setRevisionComments] = useState('');
  const [inlineComments, setInlineComments] = useState([]);
  const [authorNotes, setAuthorNotes] = useState('');
  const [showCommentsPanel, setShowCommentsPanel] = useState(true);
  const [showPreview, setShowPreview] = useState(true); // Show highlighted preview vs editor
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');

  const autoSaveTimer = useRef(null);
  const quillRef = useRef(null);
  const [activeCommentId, setActiveCommentId] = useState(null);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Create highlighted content for revision preview using TextQuoteSelector approach
  const getHighlightedContent = useCallback(() => {
    if (!content || !inlineComments.length) {
      return content;
    }

    // Create a temporary element to get plain text
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';

    // Find all matches first using TextQuoteSelector approach
    const matches = [];
    inlineComments.forEach((comment, idx) => {
      const commentId = comment.commentId || comment._id || `comment-${idx}`;

      // Use the new TextQuoteSelector matching
      const match = findTextQuoteMatch(plainText, {
        exact: comment.exact,
        prefix: comment.prefix,
        suffix: comment.suffix,
        highlightedText: comment.highlightedText
      });

      if (match) {
        matches.push({
          ...match,
          commentId,
          commentNum: idx + 1,
          isAddressed: comment.isAddressed
        });
      }
    });

    // Sort matches by position (descending) to apply highlights from end to start
    // This prevents position shifts from affecting earlier matches
    matches.sort((a, b) => b.start - a.start);

    // Apply highlights to HTML by mapping plain text positions to HTML positions
    let highlightedHtml = content;

    matches.forEach(match => {
      const highlightClass = match.isAddressed ? 'highlight-addressed' : 'highlight-pending';

      // Find the HTML position corresponding to the plain text position
      let textPos = 0;
      let htmlStartPos = -1;
      let htmlEndPos = -1;
      let inTag = false;

      // Find start position in HTML
      for (let i = 0; i < highlightedHtml.length; i++) {
        if (highlightedHtml[i] === '<') {
          inTag = true;
        } else if (highlightedHtml[i] === '>') {
          inTag = false;
        } else if (!inTag) {
          if (textPos === match.start && htmlStartPos === -1) {
            htmlStartPos = i;
          }
          textPos++;
          if (textPos === match.end) {
            htmlEndPos = i + 1;
            break;
          }
        }
      }

      // Apply highlight if we found valid positions
      if (htmlStartPos !== -1 && htmlEndPos !== -1 && htmlEndPos > htmlStartPos) {
        const textToHighlight = highlightedHtml.substring(htmlStartPos, htmlEndPos);

        // Skip if already highlighted
        if (!textToHighlight.includes('<mark')) {
          highlightedHtml =
            highlightedHtml.substring(0, htmlStartPos) +
            `<mark class="${highlightClass}" data-comment-id="${match.commentId}" data-comment-num="${match.commentNum}">${textToHighlight}</mark>` +
            highlightedHtml.substring(htmlEndPos);
        }
      }
    });

    console.log('🔍 [HIGHLIGHT DEBUG] Final result - marks added:', (highlightedHtml.match(/<mark/g) || []).length);
    return highlightedHtml;
  }, [content, inlineComments]);

  // Handle clicking on highlighted text
  const handleHighlightClick = (e) => {
    const mark = e.target.closest('mark[data-comment-id]');
    if (mark) {
      const commentId = mark.getAttribute('data-comment-id');
      setActiveCommentId(commentId);
      // Scroll the comment into view in sidebar
      const commentEl = document.querySelector(`.sidebar-comment[data-id="${commentId}"]`);
      if (commentEl) {
        commentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Handle clicking on comment number to scroll to highlight in article
  const scrollToHighlight = (commentId) => {
    setActiveCommentId(commentId);
    // Make sure we're in preview mode to see highlights
    if (!showPreview) {
      setShowPreview(true);
      // Wait for preview to render, then scroll
      setTimeout(() => {
        const mark = document.querySelector(`mark[data-comment-id="${commentId}"]`);
        if (mark) {
          mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
          mark.classList.add('highlight-flash');
          setTimeout(() => mark.classList.remove('highlight-flash'), 1500);
        }
      }, 100);
    } else {
      const mark = document.querySelector(`mark[data-comment-id="${commentId}"]`);
      if (mark) {
        mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        mark.classList.add('highlight-flash');
        setTimeout(() => mark.classList.remove('highlight-flash'), 1500);
      }
    }
  };

  // Load article for read mode
  useEffect(() => {
    if (isReadMode && viewArticleId) {
      loadArticleForReading();
    }
  }, [viewArticleId, isReadMode]);

  const loadArticleForReading = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get(`blog/${viewArticleId}`);
      // API returns blog directly, not wrapped in {success, data}
      const article = response?.data;

      if (article && article._id) {
        setArticleTitle(article.articleTitle || article.title?.name || 'Untitled');
        setContent(article.message || article.content || '');
        setTags(article.hashtags || []);
        setPublishAs(article.publishAs || 'community');
        setArticleAuthor(article.author?.fullName || (article.publishAs === 'community' ? 'Move n Muse Community' : 'Author'));
        setArticleDate(article.createdAt ? new Date(article.createdAt) : new Date());
        setTopic({ name: article.title?.name || article.articleTitle || 'Article' });
      } else {
        NotificationService.notifyError('Article not found');
      }
    } catch (error) {
      console.error('Error loading article:', error);
      NotificationService.notifyError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  // Check auth and load topic (for write mode)
  useEffect(() => {
    if (isReadMode) return; // Skip for read mode

    if (!user) {
      setLoading(false);
      return;
    }

    if (!topicId) {
      setLoading(false);
      return;
    }

    loadTopic();
  }, [user, topicId, isReadMode]);

  const loadTopic = async () => {
    try {
      // First, try to load draft - this handles both new drafts and revision articles
      const draftResponse = await ApiService.get(`topics/${topicId}/draft`);
      const draftData = draftResponse?.data;

      // If we have a revision article, allow access regardless of topic status
      if (draftData?.success && draftData?.data?.status === 'needs_revision') {
        const draft = draftData.data;
        setArticleTitle(draft.articleTitle || '');
        setContent(draft.message || '');
        setTags(draft.hashtags || []);
        setPublishAs(draft.publishAs || 'community');
        setIsRevisionMode(true);
        setRevisionComments(draft.revisionComments || '');
        setInlineComments(draft.inlineComments || []);
        setAuthorNotes(draft.authorNotes || '');

        // Get topic details for display
        const topicResponse = await ApiService.get(`topics/${topicId}`);
        if (topicResponse?.data?.success && topicResponse?.data?.data) {
          setTopic(topicResponse.data.data);
        }

        setLoading(false);
        return;
      }

      // Get topic details for regular drafts
      const topicResponse = await ApiService.get(`topics/${topicId}`);
      const topicData = topicResponse?.data;

      if (!topicData?.success || !topicData?.data) {
        NotificationService.notifyError('Topic not found');
        setLoading(false);
        return;
      }

      const topicInfo = topicData.data;
      setTopic(topicInfo);

      // Check if user has access to this topic
      const reservedById = topicInfo.reservedBy?._id || topicInfo.reservedBy;
      const currentUserId = user?._id || user?.id;

      if (topicInfo.status === 'reserved' && reservedById) {
        const reservedByStr = String(reservedById);
        const currentUserStr = String(currentUserId);

        if (reservedByStr !== currentUserStr) {
          NotificationService.notifyWarning('This topic is reserved by another user');
          setTimeout(() => navigate('/catalogue'), 2000);
          return;
        }
      }

      // Load existing draft content
      if (draftData?.success && draftData?.data) {
        const draft = draftData.data;
        if (draft.articleTitle) setArticleTitle(draft.articleTitle);
        if (draft.message) setContent(draft.message);
        if (draft.hashtags?.length > 0) setTags(draft.hashtags);
        if (draft.publishAs) setPublishAs(draft.publishAs);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading topic:', error);
      NotificationService.notifyError('Failed to load topic');
      setLoading(false);
    }
  };

  const loadDraft = async () => {
    try {
      const response = await ApiService.get(`topics/${topicId}/draft`);
      const data = response?.data;

      if (data?.success && data?.data) {
        const draft = data.data;
        if (draft.articleTitle) setArticleTitle(draft.articleTitle);
        if (draft.message) setContent(draft.message);
        if (draft.hashtags?.length > 0) setTags(draft.hashtags);
        if (draft.publishAs) setPublishAs(draft.publishAs);

        // Check if this is a revision article
        if (draft.status === 'needs_revision') {
          setIsRevisionMode(true);
          if (draft.revisionComments) {
            setRevisionComments(draft.revisionComments);
          }
        }
      }
    } catch (error) {
      // No draft exists - that's okay
      console.log('No existing draft found');
    }
  };

  // Mark an inline comment as addressed
  const markCommentAddressed = (commentId, response) => {
    setInlineComments(prev =>
      prev.map(c =>
        (c.commentId || c._id) === commentId
          ? { ...c, isAddressed: true, authorResponse: response, addressedAt: new Date() }
          : c
      )
    );
    setRespondingTo(null);
    setResponseText('');
  };

  // Count addressed comments
  const addressedCount = inlineComments.filter(c => c.isAddressed).length;
  const pendingCount = inlineComments.length - addressedCount;

  // Word count calculation
  useEffect(() => {
    const text = content.replace(/<[^>]*>/g, '').trim();
    const words = text ? text.split(/\s+/).filter(w => w.length > 0).length : 0;
    setWordCount(words);
  }, [content]);

  // Auto-save
  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
    autoSaveTimer.current = setTimeout(() => {
      saveDraft(false);
    }, 3000);
  }, [topicId, articleTitle, content, tags, publishAs]);

  useEffect(() => {
    if (topic && (articleTitle || content)) {
      triggerAutoSave();
    }
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [articleTitle, content, tags, publishAs, triggerAutoSave, topic]);

  const saveDraft = async (isManual = false) => {
    if (!topicId || saving) return;

    if (isManual) setSaving(true);
    setAutoSaveStatus('saving');

    try {
      const draftData = {
        articleTitle,
        message: content,
        hashtags: tags,
        publishAs
      };

      // Include inline comments and author notes if in revision mode
      if (isRevisionMode) {
        draftData.inlineComments = inlineComments;
        draftData.authorNotes = authorNotes;
      }

      const response = await ApiService.put(`topics/${topicId}/draft`, draftData);

      if (response?.data?.success) {
        setAutoSaveStatus('saved');
        if (isManual) {
          NotificationService.notifySuccess('Draft saved successfully!');
        }
      } else {
        setAutoSaveStatus('');
        if (isManual) {
          NotificationService.notifyError('Failed to save draft');
        }
      }
    } catch (error) {
      console.error('Save draft error:', error);
      setAutoSaveStatus('');
      if (isManual) {
        NotificationService.notifyError('Error saving draft');
      }
    } finally {
      if (isManual) setSaving(false);
      setTimeout(() => setAutoSaveStatus(''), 2000);
    }
  };

  const submitArticle = async () => {
    setSubmitAttempted(true);

    if (!articleTitle.trim()) {
      NotificationService.notifyWarning('Please enter an article title');
      return;
    }

    if (wordCount < MIN_WORDS) {
      NotificationService.notifyWarning(`Article must be at least ${MIN_WORDS} words`);
      return;
    }

    if (wordCount > MAX_WORDS) {
      NotificationService.notifyError(`Article exceeds ${MAX_WORDS} word limit`);
      return;
    }

    if (tags.length < MIN_TAGS) {
      NotificationService.notifyWarning(`Please add at least ${MIN_TAGS} tags`);
      return;
    }

    // In revision mode, require all comments to be addressed
    if (isRevisionMode && inlineComments.length > 0) {
      const unaddressedCount = inlineComments.filter(c => !c.isAddressed).length;
      if (unaddressedCount > 0) {
        NotificationService.notifyWarning(`Please address all ${unaddressedCount} remaining comment${unaddressedCount > 1 ? 's' : ''} before resubmitting`);
        return;
      }
    }

    setSubmitting(true);

    try {
      // Include inlineComments in revision mode submission
      const submitData = {
        articleTitle,
        message: content,
        hashtags: tags,
        publishAs
      };

      if (isRevisionMode) {
        submitData.inlineComments = inlineComments;
        submitData.authorNotes = authorNotes;
      }

      const response = await ApiService.put(`topics/${topicId}/submit`, submitData);

      if (response?.data?.success) {
        setShowSuccessModal(true);
      } else {
        NotificationService.notifyError(response?.data?.message || 'Failed to submit article');
      }
    } catch (error) {
      console.error('Submit error:', error);
      NotificationService.notifyError('Failed to submit article. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Tag functions
  const addTag = (tag) => {
    const cleanTag = tag.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (cleanTag && !tags.includes(cleanTag) && tags.length < MAX_TAGS) {
      setTags([...tags, cleanTag]);
    }
  };

  const removeTag = (index) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
      setTagInput('');
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  // Quill modules
  const quillModules = {
    toolbar: [
      [{ 'header': [2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['blockquote'],
      ['link'],
      ['clean']
    ]
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getWordCountClass = () => {
    if (wordCount > MAX_WORDS) return 'error';
    if (wordCount >= MIN_WORDS) return 'success';
    if (wordCount >= MIN_WORDS * 0.8) return 'warning';
    return '';
  };

  const canSubmit = wordCount >= MIN_WORDS && wordCount <= MAX_WORDS && tags.length >= MIN_TAGS;

  // Share article functionality
  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = articleTitle || 'Check out this article';
    const shareText = `${shareTitle} - Move n Muse`;

    // Try native share API first (mobile devices)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(shareUrl);
        }
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      NotificationService.notifySuccess('Link copied to clipboard!');
    }).catch(() => {
      NotificationService.notifyError('Failed to copy link');
    });
  };

  // Loading
  if (loading) {
    return (
      <div className="write-article-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Read-only mode for viewing articles
  if (isReadMode) {
    return (
      <div className="write-article-page read-mode">
        <div className="container">
          {/* Header with back and share buttons */}
          <div className="topic-header">
            <button onClick={() => navigate(-1)} className="back-btn">
              <i className="bi bi-arrow-left"></i>
            </button>
            <button onClick={handleShare} className="share-btn" title="Share article">
              <i className="bi bi-share"></i>
              <span>Share</span>
            </button>
          </div>

          {/* Paper Content - Read Only */}
          <div className="writing-container">
            <div className="paper-content">
              <h1 className="article-title-display">{articleTitle}</h1>

              <div className="author-date">
                {articleAuthor}, {articleDate ? formatDate(articleDate) : formatDate(new Date())}
              </div>

              <div className="article-content-display" dangerouslySetInnerHTML={{ __html: content }} />

              <div className="author-signature">
                <div className="signature-label">
                  {publishAs === 'community' ? 'Move n Muse Community' : articleAuthor}
                </div>
              </div>
            </div>
          </div>

          {/* Tags Display */}
          {tags.length > 0 && (
            <div className="tags-section read-only">
              <div className="tags-label">Tags</div>
              <div className="tags-container">
                <div className="tags-list">
                  {tags.map((tag, index) => (
                    <span key={index} className="tag-badge">#{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Login prompt (write mode only)
  if (!user) {
    return (
      <div className="write-article-page">
        <div className="container">
          <div className="login-prompt">
            <i className="bi bi-person-circle"></i>
            <h3>Sign In to Write</h3>
            <p>You need to be logged in to write articles.</p>
            <Link to="/login" className="login-btn">Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  // No topic selected (write mode only)
  if (!topicId) {
    return (
      <div className="write-article-page">
        <div className="container">
          <div className="no-topic">
            <i className="bi bi-journal-x"></i>
            <h3>No Topic Selected</h3>
            <p>Please select a topic from the catalogue to start writing.</p>
            <Link to="/catalogue" className="browse-topics-btn">Browse Topics</Link>
          </div>
        </div>
      </div>
    );
  }

  // Topic not found (write mode only)
  if (!topic) {
    return (
      <div className="write-article-page">
        <div className="container">
          <div className="no-topic">
            <i className="bi bi-exclamation-circle"></i>
            <h3>Topic Not Found</h3>
            <p>The topic you're looking for doesn't exist or has been removed.</p>
            <Link to="/catalogue" className="browse-topics-btn">Browse Topics</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="write-article-page">
      <div className="container">
        {/* Revision Banner - shown when editing a revision request */}
        {isRevisionMode && (
          <div className="revision-banner">
            <div className="revision-banner-icon">
              <i className="bi bi-question-circle-fill"></i>
            </div>
            <div className="revision-banner-content">
              <h4>Almost There!</h4>
              <p className="revision-intro">
                Our team has a few suggestions. Let's work together to make your article even better than it is!
              </p>
              {inlineComments.length > 0 && (
                <p className="revision-progress">
                  <i className="bi bi-list-check"></i>
                  <strong>{pendingCount}</strong> point{pendingCount !== 1 ? 's' : ''} to address
                  {addressedCount > 0 && <span className="addressed-count"> &bull; <strong>{addressedCount}</strong> completed</span>}
                </p>
              )}
              {revisionComments && (
                <div className="revision-comments-box">
                  <strong><i className="bi bi-lightbulb"></i> From Our Team:</strong>
                  <p>{revisionComments}</p>
                </div>
              )}
            </div>
            {inlineComments.length > 0 && (
              <button
                className="toggle-comments-btn"
                onClick={() => setShowCommentsPanel(!showCommentsPanel)}
              >
                <i className={`bi bi-chat-left-dots`}></i>
                {showCommentsPanel ? 'Hide' : 'View'} Feedback ({inlineComments.length})
              </button>
            )}
          </div>
        )}

        {/* Side Comments Panel - shown when in revision mode with comments */}
        {isRevisionMode && inlineComments.length > 0 && showCommentsPanel && (
          <div className="revision-comments-sidebar">
            <div className="sidebar-header">
              <span>Feedback ({addressedCount}/{inlineComments.length})</span>
              <button className="close-sidebar-btn" onClick={() => setShowCommentsPanel(false)}>
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="sidebar-comments">
              {inlineComments.map((comment, idx) => {
                const commentId = comment.commentId || comment._id;
                const isActive = activeCommentId === commentId;
                return (
                  <div
                    key={commentId || idx}
                    data-id={commentId}
                    className={`sidebar-comment ${comment.isAddressed ? 'addressed' : 'pending'} ${isActive ? 'active' : ''}`}
                  >
                    <div className="sidebar-comment-header">
                      <button
                        className="comment-num clickable"
                        onClick={() => scrollToHighlight(commentId)}
                        title="Go to text"
                      >
                        {idx + 1}
                      </button>
                      {comment.isAddressed && <i className="bi bi-check-circle-fill addressed-icon"></i>}
                    </div>
                    <div className="sidebar-feedback">{comment.comment}</div>
                    {comment.isAddressed && comment.authorResponse && (
                      <div className="sidebar-response">
                        <i className="bi bi-reply"></i> {comment.authorResponse}
                      </div>
                    )}
                    {!comment.isAddressed && (
                      <div className="sidebar-actions">
                        {respondingTo === commentId ? (
                          <div className="inline-response">
                            <input
                              type="text"
                              value={responseText}
                              onChange={(e) => setResponseText(e.target.value)}
                              placeholder="Optional: how did you fix this?"
                              onKeyPress={(e) => e.key === 'Enter' && markCommentAddressed(commentId, responseText)}
                            />
                            <div className="inline-response-btns">
                              <button onClick={() => markCommentAddressed(commentId, responseText)} title="Mark as addressed">
                                <i className="bi bi-check"></i>
                              </button>
                              <button onClick={() => { setRespondingTo(null); setResponseText(''); }}>
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button className="address-btn" onClick={() => setRespondingTo(commentId)}>
                            <i className="bi bi-check-circle"></i> Done
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="sidebar-notes">
              <textarea
                value={authorNotes}
                onChange={(e) => setAuthorNotes(e.target.value)}
                placeholder="Notes for reviewer..."
                rows={2}
              />
            </div>
          </div>
        )}

        {/* Topic Header */}
        <div className="topic-header">
          <button onClick={() => navigate(-1)} className="back-btn">
            <i className="bi bi-arrow-left"></i>
          </button>
          <div className={`word-counter ${getWordCountClass()}`}>
            {wordCount} / {MIN_WORDS}-{MAX_WORDS} words
          </div>
        </div>

        {/* Musing Guide Dropdown */}
        <div className={`musing-guide-dropdown ${guideOpen ? 'open' : ''}`}>
          <div className="musing-guide-header" onClick={() => setGuideOpen(!guideOpen)}>
            <h4><i className="bi bi-lightbulb"></i> Musing Guide</h4>
            <i className="bi bi-chevron-down toggle-icon"></i>
          </div>
          <div className="musing-guide-content">
            <div className="musing-guide-body">
              <ol>
                <li>We see musing about dance like a generous flow of sap! But for our blog posts we are inviting you to share the maple syrup thoughts that can heal hearts, or trigger profound conversations in our communities. Therefore please enjoy the playground of <strong>500-1500 words</strong>.</li>
                <li>Decide whether you would like to publish it under your name or as a <strong>Move n Muse community member</strong>.</li>
                <li>Need more time to finish your musings? Your draft is <strong>automatically saved</strong> to your account. You have <strong>2 weeks</strong> to complete your article before the topic becomes available again.</li>
                <li>We know that it's the time of heavy AI content generation, but this is a place of <strong>authenticity</strong>. Please muse with your own beautiful voice.</li>
                <li>Add <strong>5-10 tags</strong> to make it easier for others to find your article.</li>
                <li>In the future you will be invited to add an <strong>audio recording</strong> of yourself reading your musings. (Coming up!)</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Paper Writing Area */}
        <div className="writing-container">
          <div className="paper-content">
            <input
              type="text"
              className="article-title-input"
              placeholder="Your article title..."
              maxLength={200}
              value={articleTitle}
              onChange={(e) => setArticleTitle(e.target.value)}
            />

            <div className="author-date">
              {user?.fullName || 'Author'}, {formatDate(new Date())}
            </div>

            <div className="editor-wrapper">
              {/* Highlighted Preview for Revision Mode */}
              {isRevisionMode && inlineComments.length > 0 && showPreview && (
                <>
                  <div
                    className="revision-content-preview"
                    onClick={handleHighlightClick}
                    dangerouslySetInnerHTML={{ __html: getHighlightedContent() }}
                  />
                  <button
                    className="edit-content-btn"
                    onClick={() => setShowPreview(false)}
                  >
                    <i className="bi bi-pencil"></i> Edit Content
                  </button>
                </>
              )}
              {/* Regular Editor */}
              <div className={isRevisionMode && inlineComments.length > 0 && showPreview ? 'editor-hidden' : ''}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  placeholder="Start writing here..."
                />
                {isRevisionMode && inlineComments.length > 0 && !showPreview && (
                  <button
                    className="show-preview-btn"
                    onClick={() => setShowPreview(true)}
                  >
                    <i className="bi bi-eye"></i> Show Highlights
                  </button>
                )}
              </div>
            </div>

            <div className="author-signature">
              <div className="signature-label">
                {publishAs === 'community' ? 'Move n Muse Community' : (user?.fullName || 'Author')}
              </div>
              <div className="publish-as-toggle">
                <div
                  className={`publish-option ${publishAs === 'community' ? 'selected' : ''}`}
                  onClick={() => setPublishAs('community')}
                >
                  <i className="bi bi-people"></i> Community
                </div>
                <div
                  className={`publish-option ${publishAs === 'author' ? 'selected' : ''}`}
                  onClick={() => setPublishAs('author')}
                >
                  <i className="bi bi-person"></i> My Name
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tags Section */}
        <div className="tags-section">
          <div className="tags-label">
            Tags ({tags.length}/{MIN_TAGS} minimum, {MAX_TAGS} max)
            {submitAttempted && tags.length < MIN_TAGS && (
              <span className="tags-required"> - {MIN_TAGS - tags.length} more required</span>
            )}
          </div>
          <div className={`tags-container ${submitAttempted && tags.length < MIN_TAGS ? 'needs-more' : tags.length >= MIN_TAGS ? 'complete' : ''}`}>
            <div className="tags-list">
              {tags.map((tag, index) => (
                <span key={index} className="tag-badge">
                  #{tag}
                  <button type="button" onClick={() => removeTag(index)}>
                    <i className="bi bi-x"></i>
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              className="tag-input"
              placeholder={tags.length >= MAX_TAGS ? "Maximum tags reached" : "Add a tag..."}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={() => {
                if (tagInput.trim()) {
                  addTag(tagInput);
                  setTagInput('');
                }
              }}
              disabled={tags.length >= MAX_TAGS}
            />
          </div>
          <div className="tags-hint">Press Enter or comma to add tags. Add relevant keywords to help readers find your article.</div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            className="btn-action btn-save"
            onClick={() => saveDraft(true)}
            disabled={saving}
          >
            {saving ? (
              <><span className="spinner-small"></span> Saving...</>
            ) : (
              <><i className="bi bi-bookmark"></i> Save Draft</>
            )}
          </button>
          <button
            className="btn-action btn-submit"
            onClick={submitArticle}
            disabled={!canSubmit || submitting}
            title={!canSubmit ? `Need ${MIN_WORDS}-${MAX_WORDS} words and at least ${MIN_TAGS} tags to submit` : 'Submit your article for review'}
          >
            {submitting ? (
              <><span className="spinner-small"></span> Submitting...</>
            ) : (
              <><i className="bi bi-send-fill"></i> Submit for Review</>
            )}
          </button>
        </div>
      </div>

      {/* Autosave Indicator */}
      {autoSaveStatus && (
        <div className={`autosave-indicator show ${autoSaveStatus}`}>
          {autoSaveStatus === 'saving' ? (
            <><i className="bi bi-cloud-arrow-up"></i> Saving...</>
          ) : (
            <><i className="bi bi-cloud-check"></i> Draft saved</>
          )}
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-backdrop">
          <div className="success-modal">
            <div className="success-icon">
              <i className="bi bi-check-lg"></i>
            </div>
            <h4>Article Submitted!</h4>
            <p>Your article has been submitted for review. Our team will reach out within 24-48 hours.</p>
            <div className="success-actions">
              <Link to="/catalogue" className="btn-action btn-submit">Back to Catalogue</Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WriteArticlePage;
